import React, { useEffect, useMemo, useState } from "react";
import Loader from "react-spinner-loader";
import {
  X,
  History as HistoryIcon,
  PlusCircle,
  Pencil,
  Trash2,
  RefreshCcw,
  Repeat,
  ChevronUp,
  ClipboardList,
  Sheet,
} from "lucide-react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getData } from "../../../../Services/Services";
import * as EndPoints from "../../../../Services/EndPoints";
import { CheckCircle, XCircle } from "../../../../Components/IconComponents";

const AUDIT_EXPORT_COLUMNS = [
  "Event ID",
  "Action",
  "Changed By",
  "Changed On",
  "Section",
  "Item",
  "Available Qty",
  "Damaged Qty",
  "Deployed Qty",
  "Total Qty",
  "Qty To Deploy",
  "Previous Qty",
  "Updated Qty",
  "Auto",
  "Manual",
  "Status",
];

const exportFileStamp = () => {
  const p = (n) => String(n).padStart(2, "0");
  const n = new Date();
  return `${p(n.getDate())}-${p(n.getMonth() + 1)}-${n.getFullYear()}_${p(n.getHours())}${p(
    n.getMinutes()
  )}${p(n.getSeconds())}`;
};

const badgeBase = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.6,
};

const getSeverityBadgeStyle = (severity) => {
  const s = (severity || "").toLowerCase();
  let palette = { background: "#eef2f7", color: "#5b6b7a", borderColor: "#d5e0ea" };
  if (s.includes("minor") || s.includes("level 1")) {
    palette = { background: "#e6f4ea", color: "#1e7e34", borderColor: "#b7e1c1" };
  } else if (s.includes("moderate") || s.includes("level 2")) {
    palette = { background: "#fff4e0", color: "#b26a00", borderColor: "#f2d9a8" };
  } else if (s.includes("major") || s.includes("level 3")) {
    palette = { background: "#ffe9d9", color: "#c2410c", borderColor: "#f3c2a0" };
  } else if (s.includes("critical") || s.includes("level 4")) {
    palette = { background: "#fde5e5", color: "#c0392b", borderColor: "#f2b8b8" };
  }
  return {
    ...badgeBase,
    background: palette.background,
    color: palette.color,
    border: `1px solid ${palette.borderColor}`,
  };
};

// Visual treatment (color / icon) per timeline event action.
const EVENT_META = {
  CREATED: { color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: PlusCircle, label: "Created" },
  CREATE: { color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: PlusCircle, label: "Created" },
  EDIT: { color: "#1d5fa8", bg: "#e7f0fb", border: "#c2d9f0", icon: Pencil, label: "Updated" },
  UPDATE: { color: "#1d5fa8", bg: "#e7f0fb", border: "#c2d9f0", icon: Pencil, label: "Updated" },
  DELETE: { color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: Trash2, label: "Deleted" },
  DELETED: { color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: Trash2, label: "Deleted" },
};

const getEventMeta = (action) => {
  const key = (action || "").toUpperCase();
  return (
    EVENT_META[key] || {
      color: "#5b6b7a",
      bg: "#eef2f7",
      border: "#d5e0ea",
      icon: HistoryIcon,
      label: action || "Update",
    }
  );
};

const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return "NA";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh24 = d.getHours();
  const ampm = hh24 >= 12 ? "PM" : "AM";
  const hh = String(hh24 % 12 || 12).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min} ${ampm}`;
};

const showValue = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return "NA";
    }
  }
  return value;
};

const isTruthyFlag = (v) => v === true || v === 1 || v === "true" || v === "1";

const resourceNameOf = (item) => {
  if (item === null || item === undefined) return "NA";
  if (typeof item !== "object") return String(item);
  return item.resourceName || item.name || "NA";
};

// A field on a "changed" resource is either a plain (unchanged) value or a
// { from, to } pair — normalise both into the pair shape so callers don't
// need to special-case which one they got.
const asFromTo = (v) =>
  v && typeof v === "object" && ("from" in v || "to" in v) ? v : { from: v, to: v };

const resourceQuantityOf = (item) => {
  if (item === null || item === undefined || typeof item !== "object") return null;
  const q = asFromTo(item.quantity ?? item.requiredQuantity).to;
  return q === undefined ? null : q;
};

// The "quantity" field on a changed entry is a { from, to } pair.
const getChangeQuantities = (item) => {
  if (!item || typeof item !== "object") return { from: "NA", to: "NA" };
  const { from, to } = asFromTo(item.quantity);
  return { from: showValue(from), to: showValue(to) };
};

const flagIcon = (on) =>
  on ? (
    <CheckCircle style={{ color: "#28a745", width: "16px", height: "16px" }} />
  ) : (
    <XCircle style={{ color: "#dc3545", width: "16px", height: "16px" }} />
  );

// Shows the current (to) state of a boolean flag.
const flagChangeCell = (rawValue) => {
  const { to } = asFromTo(rawValue);
  const toOn = isTruthyFlag(to);
  return flagIcon(toOn);
};

const RESOURCE_DETAIL_COLUMNS = [
  { header: "Resource", cell: (item) => resourceNameOf(item) },
  { header: "Available Qty", align: "center", cell: (item) => showValue(item?.cumAvailableQuantity) },
  { header: "Damaged Qty", align: "center", cell: (item) => showValue(item?.cumDamagedQuantity) },
  { header: "Deployed Qty", align: "center", cell: (item) => showValue(item?.cumDeployedQuantity) },
  { header: "Total Qty", align: "center", cell: (item) => showValue(item?.cumTotalQuantity) },
  { header: "Qty To Deploy", align: "center", cell: (item) => showValue(resourceQuantityOf(item)) },
  { header: "Auto", align: "center", cell: (item) => flagIcon(isTruthyFlag(item?.isAutomatic)) },
  { header: "Manual", align: "center", cell: (item) => flagIcon(isTruthyFlag(item?.isManual)) },
];

const TASK_COLUMNS = [
  { header: "Task ID", cell: (item) => item?.taskSeqId || "NA" },
  { header: "Task Name", cell: (item) => item?.taskName || "NA" },
  { header: "Role", cell: (item) => item?.roleName || "NA" },
  {
    header: "Status",
    align: "center",
    cell: (item) => {
      const deleted = item?.status === "Deleted";
      return (
        <span
          style={{
            ...badgeBase,
            background: deleted ? "#fde5e5" : "#e6f4ea",
            color: deleted ? "#c0392b" : "#1e7e34",
            border: `1px solid ${deleted ? "#f2b8b8" : "#b7e1c1"}`,
          }}
        >
          {deleted ? "Deleted" : "Active"}
        </span>
      );
    },
  },
];

// Normalises the two shapes `event.resources` can take: a CREATED event
// carries `{ count, items }` (the full resource list at creation time); an
// EDIT event carries `{ added, removed, changed, unchanged, ...counts }`.
const getResourceSections = (event) => {
  const res = event?.resources;
  if (Array.isArray(res)) {
    // Older/flat API shape — treat the whole list as "created".
    return { created: res, changed: [], added: [], removed: [], unchanged: [] };
  }
  if (res && typeof res === "object") {
    if (Array.isArray(res.items)) {
      return { created: res.items, changed: [], added: [], removed: [], unchanged: [] };
    }
    return {
      created: [],
      changed: Array.isArray(res.changed) ? res.changed : [],
      added: Array.isArray(res.added) ? res.added : [],
      removed: Array.isArray(res.removed) ? res.removed : [],
      unchanged: Array.isArray(res.unchanged) ? res.unchanged : [],
    };
  }
  return { created: [], changed: [], added: [], removed: [], unchanged: [] };
};

// Same idea as getResourceSections, for the `event.tasks` block: CREATED
// carries `{ count, items }`, EDIT carries `{ added, removed, ...counts }`.
const getTaskSections = (event) => {
  const t = event?.tasks;
  if (t && typeof t === "object") {
    if (Array.isArray(t.items)) {
      return { created: t.items, added: [], removed: [] };
    }
    return {
      created: [],
      added: Array.isArray(t.added) ? t.added : [],
      removed: Array.isArray(t.removed) ? t.removed : [],
    };
  }
  return { created: [], added: [], removed: [] };
};

// The API only reports what changed to tasks on an EDIT event (added /
// removed) — not the full set still linked. To show a complete "Tasks
// Linked" table on every event (not just the ones that touched tasks), walk
// the events oldest-to-newest keeping a running map keyed by taskId (falling
// back to taskName), and snapshot it after each event: created/added tasks
// go in as "Active", removed tasks stay in the list but flip to "Deleted"
// instead of disappearing, so later snapshots keep showing their history.
const buildTaskTimeline = (rawEvents) => {
  const taskMap = new Map();
  const snapshots = [];
  const keyOf = (item) => item?.taskId ?? item?.taskName;

  rawEvents.forEach((event) => {
    const sections = getTaskSections(event);
    [...sections.created, ...sections.added].forEach((item) => {
      const key = keyOf(item);
      if (key === undefined || key === null) return;
      taskMap.set(key, {
        taskId: item?.taskId,
        taskName: item?.taskName,
        taskSeqId: item?.taskSeqId,
        roleName: item?.roleName,
        status: "Active",
      });
    });
    sections.removed.forEach((item) => {
      const key = keyOf(item);
      if (key === undefined || key === null) return;
      const existing = taskMap.get(key);
      taskMap.set(key, {
        taskId: item?.taskId ?? existing?.taskId,
        taskName: item?.taskName ?? existing?.taskName,
        taskSeqId: item?.taskSeqId ?? existing?.taskSeqId,
        roleName: item?.roleName ?? existing?.roleName,
        status: "Deleted",
      });
    });
    snapshots.push(Array.from(taskMap.values()));
  });

  return snapshots;
};

const yesNo = (v) => (v === null || v === undefined ? "NA" : isTruthyFlag(v) ? "Yes" : "No");

// Flattens every event's resource sections and linked-tasks snapshot (in
// chronological order, same as the raw API response) into export rows, one
// row per resource/task per section — mirroring what each on-screen
// SectionTable shows.
const buildExportRows = (rawEvents, taskSnapshots, fallbackId) => {
  const rows = [];
  const NA = "NA";

  rawEvents.forEach((event, idx) => {
    const common = {
      "Event ID": showValue(event.eventSeqId || fallbackId),
      Action: getEventMeta(event.action).label,
      "Changed By": showValue(event.changedByName),
      "Changed On": formatDateTime(event.changedOn),
    };

    const { created, changed, added, removed, unchanged } = getResourceSections(event);
    const taskSnapshot = taskSnapshots[idx] || [];

    const pushResourceRow = (section, item, extra = {}) => {
      rows.push({
        ...common,
        Section: section,
        Item: resourceNameOf(item),
        "Available Qty": showValue(item?.cumAvailableQuantity),
        "Damaged Qty": showValue(item?.cumDamagedQuantity),
        "Deployed Qty": showValue(item?.cumDeployedQuantity),
        "Total Qty": showValue(item?.cumTotalQuantity),
        "Qty To Deploy": showValue(resourceQuantityOf(item)),
        "Previous Qty": NA,
        "Updated Qty": NA,
        Auto: yesNo(item?.isAutomatic),
        Manual: yesNo(item?.isManual),
        Status: NA,
        ...extra,
      });
    };

    created.forEach((r) => pushResourceRow("Created", r));
    added.forEach((r) => pushResourceRow("Added", r));
    removed.forEach((r) => pushResourceRow("Removed", r));
    unchanged.forEach((r) => pushResourceRow("Unchanged", r));

    changed.forEach((c) => {
      const qty = getChangeQuantities(c);
      pushResourceRow("Updated", c, {
        "Previous Qty": qty.from,
        "Updated Qty": qty.to,
        Auto: yesNo(asFromTo(c?.isAutomatic).to),
        Manual: yesNo(asFromTo(c?.isManual).to),
      });
    });

    taskSnapshot.forEach((t) => {
      rows.push({
        ...common,
        Section: "Task",
        Item: t.taskName || "NA",
        "Available Qty": NA,
        "Damaged Qty": NA,
        "Deployed Qty": NA,
        "Total Qty": NA,
        "Qty To Deploy": NA,
        "Previous Qty": NA,
        "Updated Qty": NA,
        Auto: NA,
        Manual: NA,
        Status: t.status || "Active",
      });
    });

    if (
      created.length === 0 &&
      changed.length === 0 &&
      added.length === 0 &&
      removed.length === 0 &&
      unchanged.length === 0 &&
      taskSnapshot.length === 0
    ) {
      rows.push({
        ...common,
        Section: NA,
        Item: "No details available",
        "Available Qty": NA,
        "Damaged Qty": NA,
        "Deployed Qty": NA,
        "Total Qty": NA,
        "Qty To Deploy": NA,
        "Previous Qty": NA,
        "Updated Qty": NA,
        Auto: NA,
        Manual: NA,
        Status: NA,
      });
    }
  });

  return rows;
};

const filterLabel = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: ".4px",
  textTransform: "uppercase",
  color: "#5b6b7a",
};

// Color family per kind of change block inside an "Updated" event. Card
// header and table header share the same family so each block reads as one
// unit, and the palettes are distinct enough to scan at a glance.
const SECTION_PALETTE = {
  changes: { color: "#1d5fa8", headBg: "#e7f0fb", cellBg: "#f3f8fd", border: "#c9dcf0" },
  added: { color: "#1e7e34", headBg: "#e6f4ea", cellBg: "#f2f9f4", border: "#bfe0c8" },
  removed: { color: "#c0392b", headBg: "#fde5e5", cellBg: "#fdf3f3", border: "#f2c2c2" },
  unchanged: { color: "#475569", headBg: "#eef1f5", cellBg: "#f7f8fa", border: "#dbe1e8" },
  tasks: { color: "#7c3aed", headBg: "#f1e9fd", cellBg: "#f8f5fd", border: "#ddd0f5" },
};

// Renders one "block" of an event as its own bordered card: a colored title
// bar (icon + name + row count) sitting above a small table. Keeping every
// kind of change (created, added, removed, updated, unchanged) in this same
// shape is what makes the event body read as a clean, uniform stack.
const SectionTable = ({ icon: Icon, title, palette, columns, rows, rowKey, showSerial = true }) => {
  if (!rows.length) return null;
  return (
    <div
      style={{
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          background: palette.headBg,
        }}
      >
        <Icon size={13} color={palette.color} />
        <span style={{ fontSize: "11.5px", fontWeight: 700, color: palette.color }}>
          {title} - Total {rows.length}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "300px" }}>
          <thead>
            <tr>
              {showSerial && (
                <th
                  style={{
                    width: "50px",
                    textAlign: "center",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: palette.color,
                    padding: "5px 6px",
                    background: palette.cellBg,
                    borderBottom: `1px solid ${palette.border}`,
                  }}
                >
                  S. No
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.header}
                  style={{
                    textAlign: col.align || "left",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: palette.color,
                    padding: "5px 8px",
                    background: palette.cellBg,
                    borderBottom: `1px solid ${palette.border}`,
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={rowKey ? rowKey(row, idx) : idx} style={{ background: idx % 2 ? palette.cellBg : "#fff" }}>
                {showSerial && (
                  <td
                    style={{
                      textAlign: "center",
                      fontSize: "11px",
                      color: "#8a97a3",
                      padding: "6px",
                      borderBottom: idx === rows.length - 1 ? "none" : "1px solid #f0f2f5",
                    }}
                  >
                    {idx + 1}
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.header}
                    style={{
                      textAlign: col.align || "left",
                      fontSize: "11.5px",
                      color: "#1D2939",
                      padding: "6px 8px",
                      borderBottom: idx === rows.length - 1 ? "none" : "1px solid #f0f2f5",
                    }}
                  >
                    {col.cell(row, idx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SopResourceAuditHistoryModal = ({
  show,
  onClose,
  sopMasterId,
  sopName,
  sopMasterSeqId,
  incidentType,
  severity,
}) => {
  const [loader, setLoader] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setLoader(true);
      setError("");
      setHistoryData(null);
      try {
        const url = `${EndPoints.getResourceHistoryTimeline}/${encodeURIComponent(
          sopMasterId ?? ""
        )}`;
        const resp = await getData(url);
        if (cancelled) return;
        setHistoryData(resp && typeof resp === "object" ? resp : null);
      } catch (e) {
        if (!cancelled) {
          console.error("Error fetching SOP resource audit history:", e);
          setError("Unable to load audit history.");
        }
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [show, sopMasterId]);

  const rawEvents = useMemo(
    () => (Array.isArray(historyData?.events) ? historyData.events : []),
    [historyData]
  );

  // One "linked tasks" snapshot per event, in the same (chronological) order
  // as rawEvents — see buildTaskTimeline for why this needs the full,
  // oldest-to-newest event list rather than just the one event being shown.
  const taskSnapshots = useMemo(() => buildTaskTimeline(rawEvents), [rawEvents]);

  // Newest event first, so the most recent change is visible without scrolling.
  const events = useMemo(() => [...rawEvents].reverse(), [rawEvents]);

  const handleExportExcel = async () => {
    const rows = buildExportRows(
      rawEvents,
      taskSnapshots,
      sopMasterSeqId || historyData?.sopMasterSeqId
    );
    if (!rows.length) {
      toast.info("No audit history to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("SOP Audit History");

    const headerRow = ws.addRow(AUDIT_EXPORT_COLUMNS);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E6DA4" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    rows.forEach((r) => {
      const dataRow = ws.addRow(AUDIT_EXPORT_COLUMNS.map((col) => r[col]));
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    ws.columns.forEach((col, idx) => {
      col.width = idx === 5 ? 30 : idx <= 3 ? 20 : 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const idPart = historyData?.sopMasterSeqId || sopMasterSeqId || "sop";
    saveAs(blob, `SOP_Audit_History_${idPart}_${exportFileStamp()}.xlsx`);
  };

  if (!show) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,28,38,0.55)",
        zIndex: 3000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "min(760px, 96vw)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon size={14} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>
              SOP Audit History
            </div>
            <div style={{ fontSize: "10.5px", color: "rgba(255,255,255,0.8)", marginTop: "1px" }}>
              Timeline of resource changes for the selected SOP
            </div>
          </div>
          <span
            onClick={handleExportExcel}
            title="Download Excel"
            style={{
              cursor: "pointer",
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginRight: "8px",
            }}
          >
            <Sheet size={14} color="#fff" />
          </span>
          <span
            onClick={onClose}
            title="Close"
            style={{
              cursor: "pointer",
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={16} color="#fff" />
          </span>
        </div>

        {/* Context bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
            padding: "10px 16px",
            borderBottom: "1px solid #eef1f4",
            background: "#f8fafc",
          }}
        >
          <span style={{ ...filterLabel, marginRight: "2px" }}>SOP</span>
          <span style={{ ...badgeBase, background: "#1d5fa8", color: "#fff", border: "1px solid transparent" }}>
            {showValue(sopMasterSeqId || historyData?.sopMasterSeqId)}
          </span>
          <span style={{ fontSize: "11.5px", fontWeight: 600, color: "#1a2e35" }}>
            {showValue(sopName)}
          </span>
          <span style={{ ...filterLabel, margin: "0 2px 0 10px" }}>Incident Type</span>
          <span style={{ ...badgeBase, background: "#eef3f8", color: "#1a2e35", border: "1px solid #d9e2ea" }}>
            {showValue(incidentType)}
          </span>
          <span style={{ ...filterLabel, margin: "0 2px 0 10px" }}>Severity</span>
          <span style={getSeverityBadgeStyle(severity)}>{showValue(severity)}</span>
        </div>

        {/* Body */}
        <div style={{ position: "relative", overflowY: "auto", padding: "18px 20px" }}>
          <Loader show={loader} type="body" message="Loading Audit History" />

          {!loader && error && (
            <div style={{ textAlign: "center", color: "#c0392b", fontSize: "12px", padding: "30px 0" }}>
              {error}
            </div>
          )}

          {!loader && !error && events.length === 0 && (
            <div style={{ textAlign: "center", color: "#5b6b7a", fontSize: "12px", padding: "30px 0" }}>
              No audit history found for this record.
            </div>
          )}

          {!loader && !error && events.length > 0 && (
            <div style={{ position: "relative" }}>
              {/* Vertical timeline rail — events are listed newest-first (top),
                  so this line (and the arrow markers along it, below) reads
                  bottom-to-top: oldest event at the bottom, flowing up to the
                  latest one at the top. */}
              <div
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "6px",
                  bottom: "6px",
                  width: "3px",
                  borderRadius: "2px",
                  background: "#c3ccd6",
                }}
              />
              {events.map((event, idx) => {
                const meta = getEventMeta(event.action);
                const Icon = meta.icon;
                const isLatest = idx === 0;
                const { created: resources, changed, added, removed, unchanged } =
                  getResourceSections(event);
                // events is rawEvents reversed, so this event's chronological
                // position (and its matching task snapshot) is the mirror index.
                const taskSnapshot = taskSnapshots[rawEvents.length - 1 - idx] || [];

                const isLastEvent = idx === events.length - 1;

                return (
                  <React.Fragment key={event.eventSeqId || `${event.action}-${idx}`}>
                    <div
                      style={{
                        position: "relative",
                        paddingLeft: "42px",
                      }}
                    >
                      {/* Node */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          background: meta.bg,
                          border: `2px solid ${meta.color}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                          boxShadow: isLatest ? `0 0 0 4px ${meta.bg}` : "none",
                        }}
                      >
                        <Icon size={14} color={meta.color} />
                      </div>

                      {/* Card */}
                      <div
                        style={{
                          border: `1px solid ${meta.border}`,
                          borderRadius: "10px",
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            background: meta.bg,
                            borderBottom: `1px solid ${meta.border}`,
                          }}
                        >
                          {/* The CREATE event has no eventSeqId of its own — fall back to the
                              SOP's own master seq id so it still carries an id badge like every
                              other event on the timeline. */}
                          {(event.eventSeqId || historyData?.sopMasterSeqId) && (
                            <span
                              style={{
                                fontSize: "10.5px",
                                fontWeight: 600,
                                color: "#5b6b7a",
                                fontFamily: "monospace",
                              }}
                            >
                              {event.eventSeqId || historyData?.sopMasterSeqId}
                            </span>
                          )}
                          <span
                            style={{
                              ...badgeBase,
                              background: meta.color,
                              color: "#fff",
                              border: "1px solid transparent",
                            }}
                          >
                            {meta.label}
                          </span>
                          {isLatest && (
                            <span
                              style={{
                                ...badgeBase,
                                background: "#fff",
                                color: meta.color,
                                border: `1px solid ${meta.color}`,
                              }}
                            >
                              Latest
                            </span>
                          )}
                          <span style={{ marginLeft: "auto", fontSize: "11.5px", fontWeight: 700, color: meta.color }}>
                            {showValue(event.changedByName)}
                          </span>
                          <span style={{ fontSize: "10.5px", color: "#5b6b7a" }}>
                            {formatDateTime(event.changedOn)}
                          </span>
                        </div>

                        <div style={{ padding: "10px 12px" }}>
                          {/* CREATE: resource list, same card+table shape as the Updated event blocks */}
                          <SectionTable
                            icon={PlusCircle}
                            title="Resources Created"
                            palette={SECTION_PALETTE.added}
                            rows={resources}
                            columns={RESOURCE_DETAIL_COLUMNS}
                          />

                          {/* EDIT/UPDATE: each kind of change gets its own stacked card+table, in a fixed
                              order (quantity changes, then additions, removals, unchanged) so the
                              layout never depends on what happens to be present. */}
                          <SectionTable
                            icon={RefreshCcw}
                            title="Resources Updated"
                            palette={SECTION_PALETTE.changes}
                            rows={changed}
                            columns={[
                              { header: "Resource", cell: (item) => resourceNameOf(item) },
                              {
                                header: "Available Qty",
                                align: "center",
                                cell: (item) => showValue(item?.cumAvailableQuantity),
                              },
                              {
                                header: "Damaged Qty",
                                align: "center",
                                cell: (item) => showValue(item?.cumDamagedQuantity),
                              },
                              {
                                header: "Deployed Qty",
                                align: "center",
                                cell: (item) => showValue(item?.cumDeployedQuantity),
                              },
                              {
                                header: "Total Qty",
                                align: "center",
                                cell: (item) => showValue(item?.cumTotalQuantity),
                              },
                              {
                                header: "Previous Qty",
                                align: "center",
                                cell: (item) => getChangeQuantities(item).from,
                              },
                              {
                                header: "Updated Qty",
                                align: "center",
                                cell: (item) => getChangeQuantities(item).to,
                              },
                              {
                                header: "Auto",
                                align: "center",
                                cell: (item) => flagChangeCell(item?.isAutomatic),
                              },
                              {
                                header: "Manual",
                                align: "center",
                                cell: (item) => flagChangeCell(item?.isManual),
                              },
                            ]}
                          />

                          <SectionTable
                            icon={PlusCircle}
                            title="Resources Added"
                            palette={SECTION_PALETTE.added}
                            rows={added}
                            columns={RESOURCE_DETAIL_COLUMNS}
                          />

                          <SectionTable
                            icon={Trash2}
                            title="Resources Removed"
                            palette={SECTION_PALETTE.removed}
                            rows={removed}
                            columns={RESOURCE_DETAIL_COLUMNS}
                          />

                          <SectionTable
                            icon={Repeat}
                            title="Unchanged Resources"
                            palette={SECTION_PALETTE.unchanged}
                            rows={unchanged}
                            columns={RESOURCE_DETAIL_COLUMNS}
                          />

                          {/* Every event shows the same "Tasks Linked" table — the full set of
                              tasks tied to the SOP as of this event, each tagged Active/Deleted —
                              rather than separate Added/Removed tables that would disappear on
                              events touching no tasks. */}
                          <SectionTable
                            icon={ClipboardList}
                            title="Tasks Linked"
                            palette={SECTION_PALETTE.tasks}
                            rows={taskSnapshot}
                            columns={TASK_COLUMNS}
                            showSerial={false}
                          />

                          {resources.length === 0 &&
                            changed.length === 0 &&
                            added.length === 0 &&
                            removed.length === 0 &&
                            unchanged.length === 0 &&
                            taskSnapshot.length === 0 && (
                              <div style={{ fontSize: "11px", color: "#8a97a3" }}>No details available.</div>
                            )}
                        </div>
                      </div>
                    </div>

                    {/* Connector arrow — points up the rail toward the next (newer)
                        event, so the timeline direction (oldest at the bottom,
                        latest at the top) is unambiguous. */}
                    {!isLastEvent && (
                      <div style={{ position: "relative", height: "34px" }}>
                        <div
                          title="Older → Newer"
                          style={{
                            position: "absolute",
                            left: "3px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "26px",
                            height: "26px",
                            borderRadius: "50%",
                            background: "#fff",
                            border: "2px solid #9aa8b6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                          }}
                        >
                          <ChevronUp size={18} color="#5b6b7a" strokeWidth={3} />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid #eef1f4",
            display: "flex",
            justifyContent: "flex-end",
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: "32px",
              fontSize: "12px",
              fontWeight: 600,
              padding: "0 18px",
              borderRadius: "6px",
              cursor: "pointer",
              border: "1px solid #2E6DA4",
              color: "#fff",
              background: "#2E6DA4",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SopResourceAuditHistoryModal;
